import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Building2 } from "lucide-react";
import { initUTMTracking } from "@/lib/utm";
import { trackPartnerView, trackPartnerSubmit } from "@/lib/analytics";
import { useEffect } from "react";

const PartnerForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Track partner form view on mount
  useEffect(() => {
    trackPartnerView();
  }, []);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    vertical: "",
    avg_ticket_aed: "",
    city: "",
    notes: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Track partner submit event
      trackPartnerSubmit(formData.email, formData.company_name, formData.vertical);

      // Use client supabase for frontend insert (anon role)
      const { error } = await supabase
        .from('partners')
        .insert([{
          ...formData,
          avg_ticket_aed: formData.avg_ticket_aed ? parseInt(formData.avg_ticket_aed) : null,
          created_at: new Date().toISOString()
        }]);

      if (error) {
        // Log the error for analytics
        await supabase.functions.invoke('analytics-track', {
          body: {
            event_type: 'insert_error',
            email: formData.email,
            source: 'partner_form',
            metadata: { 
              table: 'partners',
              error_code: error.code,
              error_message: error.message,
              company_name: formData.company_name
            }
          }
        });
        throw error;
      }

      toast({
        title: "Application Submitted! 🤝",
        description: "We'll review your partnership application and be in touch within 2-3 business days.",
      });

      // Reset form
      setFormData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        website: "",
        vertical: "",
        avg_ticket_aed: "",
        city: "",
        notes: ""
      });

    } catch (error) {
      console.error('Partner form error:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section className="py-20 px-4 bg-flexaura-teal/10">
      <div className="max-w-2xl mx-auto">
        <Card className="card-shadow">
          <CardHeader className="text-center">
            <div className="w-16 h-16 gradient-bg rounded-lg flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-heading">Become a Partner</CardTitle>
            <CardDescription className="text-lg">
              Join the Flexaura network and offer flexible payments to your customers
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="Your Company Ltd"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    required
                    value={formData.contact_name}
                    onChange={(e) => handleInputChange('contact_name', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="john@company.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+971 50 123 4567"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://yourcompany.com"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vertical">Industry *</Label>
                  <Select value={formData.vertical} onValueChange={(value) => handleInputChange('vertical', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Travel">Travel</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="avg_ticket_aed">Average Ticket (AED)</Label>
                  <Input
                    id="avg_ticket_aed"
                    type="number"
                    value={formData.avg_ticket_aed}
                    onChange={(e) => handleInputChange('avg_ticket_aed', e.target.value)}
                    placeholder="25000"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Dubai"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Tell us more about your business and how Flexaura can help..."
                  rows={4}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                variant="gradient"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit Partnership Application"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PartnerForm;